import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  Agent,
  scheduleLabel,
  AgentTrigger,
  ProjectPlan,
  runStatus,
  TriggerRun,
  triggersErrorMessage,
} from '../../models/ai-agents.models';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';

/** The runs of one trigger as loaded in the support view. */
export interface TriggerRunsState {
  open: boolean;
  loading: boolean;
  error: string;
  items: TriggerRun[];
  total: number;
  /** The next page to read. */
  page: number;
}

export const RUNS_PAGE_SIZE = 20;

/** "lead__find_lead" -> "lead · find_lead" (the service prefix of an MCP tool name). */
export function triggerToolLabel(name: string): string {
  const i = name.indexOf('__');
  return i > 0 ? name.slice(0, i) + ' · ' + name.slice(i + 2) : name;
}

/** Why a trigger does not run now; '' when it runs on its schedule. */
export function triggerOffReason(t: AgentTrigger, plan: ProjectPlan | null): string {
  if (plan && plan.allowTriggers === false) return 'The project plan does not include automations, so no run happens.';
  if (plan && plan.enabled === false) return 'The project plan is switched off, so runs are skipped.';
  if (!t.enabled) {
    if (t.disabledReason) return 'Switched itself off: ' + t.disabledReason;
    return 'Switched off by the customer.';
  }
  if (!t.nextRunAt && !t.nextRunLocal) return 'No next run (a one-time trigger that already ran).';
  return '';
}

/** The schedule in words: the service's scheduleText, else our own words from the fields. */
export function triggerScheduleText(t: Pick<AgentTrigger, 'scheduleText' | 'scheduleKind' | 'schedule'>): string {
  return t.scheduleText || scheduleLabel(t.scheduleKind, t.schedule);
}

/** Run duration in seconds, or null while running / not run. */
export function runSeconds(r: Pick<TriggerRun, 'startedAt' | 'finishedAt'>): number | null {
  if (!r.startedAt || !r.finishedAt) return null;
  const ms = Date.parse(r.finishedAt) - Date.parse(r.startedAt);
  return Number.isFinite(ms) && ms >= 0 ? Math.round(ms / 100) / 10 : null;
}

/**
 * Contract v1.6, support view (system scope, read-only): the agent's automations (TIMER triggers) and the runs of each.
 * The customer creates, edits, switches and runs them in the agents Dashboard; nothing is changed here.
 */
@Component({
  selector: 'app-ai-agent-triggers',
  templateUrl: './agent-triggers.component.html',
  styleUrls: ['../../ai-agents.shared.css', './agent-triggers.component.css'],
})
export class AgentTriggersComponent implements OnChanges {
  @Input() agent!: Agent;
  /** The plan of the agent's project, for the notes only; null = unknown. */
  @Input() plan: ProjectPlan | null = null;

  readonly runStatus = runStatus;
  readonly triggerToolLabel = triggerToolLabel;
  readonly triggerOffReason = triggerOffReason;
  readonly triggerScheduleText = triggerScheduleText;
  readonly runSeconds = runSeconds;

  triggers: AgentTrigger[] = [];
  loading = false;
  error = '';
  /** Runs per trigger id. */
  runs: Record<string, TriggerRunsState> = {};
  /** Run ids whose full answer is shown. */
  expanded = new Set<string>();

  constructor(private api: AiAgentsApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['agent'] && this.agent) {
      this.load();
    }
  }

  get planOff(): boolean {
    return !!this.plan && this.plan.allowTriggers === false;
  }

  get planMax(): number | null {
    return this.plan && typeof this.plan.maxTriggersPerAgent === 'number' ? this.plan.maxTriggersPerAgent : null;
  }

  get planRunsPerMonth(): number | null {
    return this.plan && typeof this.plan.maxTriggerRunsPerMonth === 'number' ? this.plan.maxTriggerRunsPerMonth : null;
  }

  get enabledCount(): number {
    return this.triggers.filter((t) => t.enabled).length;
  }

  load(): void {
    const agentId = this.agent.id;
    this.loading = true;
    this.error = '';
    this.runs = {};
    this.expanded.clear();
    this.api.listAgentTriggers(agentId).subscribe({
      next: (list) => {
        if (this.agent?.id !== agentId) return;
        this.triggers = Array.isArray(list) ? list : [];
        this.loading = false;
      },
      error: (err) => {
        if (this.agent?.id !== agentId) return;
        this.triggers = [];
        this.loading = false;
        this.error = triggersErrorMessage(err, 'Loading the automations failed.');
      },
    });
  }

  /** Opens (and reads the first page of) or closes the run history of a trigger. */
  toggleRuns(t: AgentTrigger): void {
    const state = this.runs[t.id];
    if (state?.open) {
      state.open = false;
      return;
    }
    if (state && !state.error && state.items.length) {
      state.open = true;
      return;
    }
    this.runs[t.id] = { open: true, loading: false, error: '', items: [], total: 0, page: 0 };
    this.loadRuns(t);
  }

  /** The run history of this trigger is shown. */
  isOpen(t: AgentTrigger): boolean {
    return !!this.runs[t.id]?.open;
  }

  hasMoreRuns(t: AgentTrigger): boolean {
    const s = this.runs[t.id];
    return !!s && !s.loading && !s.error && s.items.length < s.total;
  }

  /** Reads the next page of runs and adds it below the loaded ones. */
  loadRuns(t: AgentTrigger): void {
    const agentId = this.agent.id;
    const state = this.runs[t.id];
    if (!state || state.loading) return;
    state.loading = true;
    state.error = '';
    this.api.listTriggerRuns(agentId, t.id, state.page, RUNS_PAGE_SIZE).subscribe({
      next: (res) => {
        if (this.agent?.id !== agentId || this.runs[t.id] !== state) return;
        const known = new Set(state.items.map((r) => r.id));
        const items = Array.isArray(res?.items) ? res.items : [];
        state.items = [...state.items, ...items.filter((r) => !known.has(r.id))].sort((a, b) => (b.seq ?? 0) - (a.seq ?? 0));
        state.total = typeof res?.total === 'number' ? res.total : state.items.length;
        state.page += 1;
        state.loading = false;
      },
      error: (err) => {
        if (this.agent?.id !== agentId || this.runs[t.id] !== state) return;
        state.loading = false;
        state.error = triggersErrorMessage(err, 'Loading the runs failed.');
      },
    });
  }

  toggleAnswer(r: TriggerRun): void {
    if (this.expanded.has(r.id)) this.expanded.delete(r.id);
    else this.expanded.add(r.id);
  }
}
